/**
 * ParentConsoleRoute — `/foreldre`, the parent cockpit.
 *
 * Everything a parent does to run the chore loop used to be scattered: the
 * template library was section six of eight inside a single Card at
 * /settings/admin, wedged between a push-health table and AI cost telemetry,
 * while the approval queue lived on its own route with no link between them.
 *
 * Three tabs, one place:
 *   Barna       — per-child cards with inline point adjustment, then the
 *                 «N medlemmer · Inviter / legg til» row that opens the member
 *                 admin list. Standardfanen.
 *   Oppgavemaler — today's spawned cards (fix what the cron got wrong), then
 *                 the chore-template library.
 *   System      — AdminDashboardPanel: kjøkkenskjermer, AI i dag og radene
 *                 med drill-down (butikkartikler, push, AI-drift, tving
 *                 oppdatering, retningslinjer). /settings/admin viser det samme.
 *
 * #155 (redesign steg 5): Godkjenning-fanen er borte. Køen er hovedmenyfanen
 * Godkjenn på /godkjenninger, og `?fane=approvals` videresendes dit.
 *
 * #159 (redesign steg 9a, Stell_Familie): ScreenHeader «Familie» med avataren
 * (skallet tegner ikke sin egen topp her, `drawsOwnHeader`), og fanene som
 * `SegmentedTabs`-lenker. Fanen ligger i søkestrengen, så lenkene får `active`
 * fra `?fane=` i stedet for NavLink sin stisammenligning.
 *
 * #159 (steg 9b, Stell_Maler): Oppgavemaler har «Ny mal» i toppen, kortet
 * «Oppgaver i dag» med tellefliser (InstanceControlSection) og malbiblioteket
 * under. Skjemavisningen eies her, fordi knappen står i toppen; mens et skjema
 * er åpent, er knappen og flisene borte, så skjermen bare har skjemaet.
 *
 * Auth: gated on /api/auth/me role === 'admin', the same client-side pattern
 * ApprovalsRoute uses. The server is the authoritative gate on every mutation
 * (requireAdminScope), so this is UX rather than security.
 *
 * The tab lives in the URL as ?fane= so a parent can bookmark or reload
 * straight back into it.
 */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router';
import { Button, Icon, SegmentedTabs } from '../../components/primitives';
import { ScreenHeader } from '../../components/ScreenHeader';
import { useSession } from '../auth/useSession';
import { AdminDashboardPanel } from '../admin/AdminDashboardPanel';
import { ChoreTemplateAdminSection, type TemplateView } from '../admin/ChoreTemplateAdminSection';
import { InstanceControlSection } from './InstanceControlSection';
import { FamilyTab } from './FamilyTab';

const TABS = ['family', 'chores', 'system'] as const;
type Tab = (typeof TABS)[number];

/** Som Premier-rammen (PREMIER_MAIN): topp, faner og kort med 12 px mellomrom. */
export const CONSOLE_MAIN = 'flex min-h-dvh flex-col gap-3 px-5 pt-4 pb-6 md:px-8';

function isTab(v: string | null): v is Tab {
  return v !== null && (TABS as readonly string[]).includes(v);
}

export function ParentConsoleRoute(): ReactNode {
  const { t } = useTranslation();
  const { data: session, isLoading } = useSession();
  const [searchParams] = useSearchParams();
  const [templateView, setTemplateView] = useState<TemplateView>({ kind: 'list' });

  const param = searchParams.get('fane');
  const tab: Tab = isTab(param) ? param : 'family';

  // Skjemaet hører til Oppgavemaler-fanen. Uten denne ville det stå åpent med
  // gammel rad når forelderen kommer tilbake, mens utkastet i det er borte.
  if (tab !== 'chores' && templateView.kind !== 'list') {
    setTemplateView({ kind: 'list' });
  }

  /** Standardfanen skrives uten ?fane=, så adressen er den korte. */
  function hrefFor(key: Tab): string {
    const params = new URLSearchParams(searchParams);
    if (key === 'family') params.delete('fane');
    else params.set('fane', key);
    const qs = params.toString();
    return qs ? `/foreldre?${qs}` : '/foreldre';
  }

  // #153: Godkjenn er en egen fane i hovedmenyen nå. Gamle lenker og bokmerker
  // til konsollens godkjenningsfane lander i køen der.
  if (param === 'approvals') {
    return <Navigate to="/godkjenninger" replace />;
  }

  if (isLoading) {
    return (
      // Skallet tegner ikke toppen her (drawsOwnHeader), så også laste- og
      // 403-visningen har ScreenHeader med avataren til Meg.
      <main data-testid="parent-console-loading" className={CONSOLE_MAIN}>
        <ScreenHeader title={t('nav.family')} />
        <p className="text-ink-2 text-sm">{t('admin.console.loading')}</p>
      </main>
    );
  }

  const isAdmin = session?.member?.role === 'admin';
  if (!isAdmin) {
    return (
      <main data-testid="parent-console-403" className={CONSOLE_MAIN}>
        <ScreenHeader title={t('nav.family')} />
        <p className="text-ink-2 flex flex-1 items-center justify-center text-sm">
          {t('admin.console.notAdmin')}
        </p>
      </main>
    );
  }

  return (
    <main data-testid="parent-console" className={CONSOLE_MAIN}>
      <ScreenHeader
        title={t('nav.family')}
        trailing={
          tab === 'chores' && templateView.kind === 'list' ? (
            <Button
              size={44}
              onClick={() => setTemplateView({ kind: 'create' })}
              data-testid="chore-template-add"
            >
              <Icon.Plus size={18} />
              {t('admin.choreTemplates.add')}
            </Button>
          ) : null
        }
      />

      <SegmentedTabs
        variant="links"
        label={t('admin.console.tabsLabel')}
        data-testid="parent-console-tabs"
        className="md:max-w-xl"
        links={TABS.map((key) => ({
          to: hrefFor(key),
          label: t(`admin.console.tabs.${key}`),
          active: tab === key,
          testId: `parent-console-tab-${key}`,
        }))}
      />

      {/* Oppgaver = today's spawned cards first, then the template library.
          The order is deliberate: a parent arriving here usually wants to fix
          something that already happened, not author a new template. */}
      {tab === 'chores' ? (
        <div className="flex flex-col gap-3" data-testid="parent-console-chores">
          {templateView.kind === 'list' ? <InstanceControlSection /> : null}
          <ChoreTemplateAdminSection view={templateView} onViewChange={setTemplateView} />
        </div>
      ) : null}

      {tab === 'family' ? <FamilyTab /> : null}

      {/* #159 (steg 9c): System-fanen er Admin-panelet selv, ikke lenger en
          lenke videre til /settings/admin (som fortsatt viser det samme). */}
      {tab === 'system' ? (
        <div data-testid="parent-console-system">
          <AdminDashboardPanel />
        </div>
      ) : null}
    </main>
  );
}
